import type { CaraFloatingOptions, CaraInstance } from '@wix/cara-sdk';
import { createCrossTabLock, type CrossTabLock } from '@wix/wix-chatbot-common/browser';
import { CARA_CHATBOT_FULLPAGE_HOST } from '@wix/wix-help-widget-common/types';
import { v4 as uuidv4 } from 'uuid';

import { getRuntimeDataForBi } from '../../bi/utils/getRuntimeDataForBi';
import { CARA_REFERRAL } from '../../common/hooks/useIsCaraSession';
import { widgetActions } from '../../common/reducers/widget';
import { selectEditorType } from '../../common/selectors/homepage';
import { selectSelectedSite } from '../../common/selectors/sites';
import { selectWidgetSessionId } from '../../common/selectors/widgetSession';
import { store } from '../../common/store';
import { loadChatbotStartChat } from '../../common/thunks/chatbotStartChat.thunk';
import { widgetSDK } from '../../sdk/widgetSDK';
import { emitter } from '../../util/emitter';
import { logger } from '../../util/logger';
import { renderModel } from '../../util/renderModel';
import {
  CARA_FULLPAGE_ANCHOR_DATA_HOOK,
  CARA_FULLPAGE_HOST_ATTRIBUTE,
  isFullPageCaraEnabled,
} from './isFullPageCaraEnabled';

const CARA_CONTACT_FLOW_NODE_ID = '5293f83f-910d-45bb-a776-c0b2e3a73b9d';
const FEEDBACK_MIN_CALL_DURATION_MS = 30_000;
const CALL_END_REPORT_TIMEOUT_MS = 3_000;
const DESTROY_DELAY_MS = 100;
const REMOTE_LOCK_RECHECK_MS = 30_000;
const NO_DRAG_BOUNDS = { top: 0, left: 0, right: 0, bottom: 0 };

let session: CaraInstance | null = null;
let lock: CrossTabLock | null = null;
let initialized = false;
let streamingStartTime: number | null = null;
let escalatingToContactFlow = false;
let activeConversationId: string | undefined;
let callEndReported = false;
let remoteLockRecheckTimer: ReturnType<typeof setInterval> | null = null;

const isSessionActive = (): boolean => session !== null;

const isSessionBlocked = (): boolean => isSessionActive() || store.getState().widget.caraActiveInOtherTab;

const syncRemoteLockState = (active: boolean): void => {
  store.dispatch(widgetActions.setCaraActiveInOtherTab(active));
  if (active) {
    if (remoteLockRecheckTimer === null) {
      remoteLockRecheckTimer = setInterval(() => lock?.check(), REMOTE_LOCK_RECHECK_MS);
    }
    return;
  }
  if (remoteLockRecheckTimer !== null) {
    clearInterval(remoteLockRecheckTimer);
    remoteLockRecheckTimer = null;
  }
};

const ensureLock = (): CrossTabLock => {
  if (!lock) {
    lock = createCrossTabLock({
      tabId: uuidv4(),
      isSessionActive,
      onStateChange: syncRemoteLockState,
    });
  }
  return lock;
};

const showGate = (): void => {
  store.dispatch(widgetActions.setCaraSessionInConversation(true));
  document.documentElement.setAttribute(CARA_FULLPAGE_HOST_ATTRIBUTE, '');
  store.dispatch(widgetActions.setCaraFullPageSessionActive(true));
};

const hideGate = (): void => {
  document.documentElement.removeAttribute(CARA_FULLPAGE_HOST_ATTRIBUTE);
  store.dispatch(widgetActions.setCaraFullPageSessionActive(false));
};

const abandonGate = (): void => {
  hideGate();
  store.dispatch(widgetActions.setCaraSessionInConversation(false));
};

const wasCallLongEnoughToRate = (elapsedMs: number): boolean =>
  streamingStartTime !== null && elapsedMs >= FEEDBACK_MIN_CALL_DURATION_MS;

const requestPostCallFeedback = (elapsedMs: number): void => {
  if (escalatingToContactFlow) {
    store.dispatch(widgetActions.setCaraSurveyPending(true));
    return;
  }
  if (wasCallLongEnoughToRate(elapsedMs)) {
    emitter.emit('caraCallEnded', undefined);
  }
};

const reportCallEnded = async (durationMs: number): Promise<void> => {
  const conversationId = activeConversationId;
  if (callEndReported || streamingStartTime === null || !conversationId) {
    return;
  }
  callEndReported = true;
  try {
    const { reportCaraCallEnded } = await import('./reportCaraCallEnded');
    await reportCaraCallEnded({ conversationId, durationMs });
  } catch (error) {
    logger.error({
      error: error instanceof Error ? error : new Error(String(error)),
      message: 'Failed to report the Cara call end',
    });
  }
};

const escalateToContactFlow = async (elapsedMs: number): Promise<void> => {
  if (escalatingToContactFlow) {
    return;
  }
  escalatingToContactFlow = true;
  await Promise.race([
    reportCallEnded(elapsedMs),
    new Promise((resolve) => setTimeout(resolve, CALL_END_REPORT_TIMEOUT_MS)),
  ]);
  try {
    const context = await widgetSDK.getContext();
    const state = store.getState();
    const selectedSite = selectSelectedSite(state);
    if (selectedSite?.id) {
      context.metaSiteId = selectedSite.id;
    }
    await store
      .dispatch(
        loadChatbotStartChat({
          context,
          editorType: selectEditorType(state),
          params: { nodeId: CARA_CONTACT_FLOW_NODE_ID, referral: CARA_REFERRAL },
        }),
      )
      .unwrap();
  } catch (error) {
    escalatingToContactFlow = false;
    throw error;
  }
};

const attachSessionLifecycle = (instance: CaraInstance): void => {
  instance.on('started', ({ streamingStartTime: startedAt }) => {
    if (session !== instance) {
      return;
    }
    streamingStartTime = startedAt;
  });

  instance.on('stopped', ({ elapsedMs }) => {
    if (session !== instance) {
      return;
    }
    session = null;
    lock?.clear();
    hideGate();
    void reportCallEnded(elapsedMs);
    requestPostCallFeedback(elapsedMs);
    streamingStartTime = null;
    escalatingToContactFlow = false;
    activeConversationId = undefined;
    callEndReported = false;

    setTimeout(() => instance.destroy(), DESTROY_DELAY_MS);
  });
};

export const startFullPageCaraSession = async ({
  sessionReferral,
  conversationId,
}: {
  sessionReferral: string;
  conversationId?: string;
}): Promise<void> => {
  if (isSessionBlocked()) {
    return;
  }

  let instance: CaraInstance | null = null;

  try {
    const state = store.getState();
    const { mountFloating } = await import('@wix/cara-sdk');
    const { metaSiteId } = await getRuntimeDataForBi(state);

    if (isSessionBlocked()) {
      return;
    }

    showGate();

    const gateOptions: CaraFloatingOptions = {
      gateAnchor: `[data-hook="${CARA_FULLPAGE_ANCHOR_DATA_HOOK}"]`,
      gateDragBounds: () => NO_DRAG_BOUNDS,
      onGateBack: () => session?.stop(),
      disableInGateFeedback: true,
      onContactFlowConnect: escalateToContactFlow,
    };

    instance = mountFloating(
      {
        contextPlatform: CARA_CHATBOT_FULLPAGE_HOST,
        conversationId: conversationId?.trim() || undefined,
        contextMsid: metaSiteId,
        selfServiceSessionId: selectWidgetSessionId(state),
        sessionReferral,
        user: {
          userId: renderModel.getUserId(),
          loggedInAccountId: renderModel.getAccountId(),
        },
      },
      gateOptions,
    );

    if (!instance) {
      abandonGate();
      return;
    }

    session = instance;
    activeConversationId = conversationId?.trim() || undefined;
    callEndReported = false;
    ensureLock().write();
    attachSessionLifecycle(instance);

    await instance.start();
  } catch (error) {
    if (instance && session !== instance) {
      return;
    }
    logger.error({
      error: error instanceof Error ? error : new Error(String(error)),
      message: 'Failed to start the Cara full page session',
    });
    if (instance) {
      session = null;
      activeConversationId = undefined;
      lock?.clear();
      instance.destroy();
    }
    abandonGate();
  }
};

export const initFullPageCara = (): void => {
  if (initialized || !isFullPageCaraEnabled()) {
    return;
  }
  initialized = true;

  const caraLock = ensureLock();

  window.addEventListener('storage', (event) => caraLock.handleStorageEvent(event));
  window.addEventListener('pagehide', () => caraLock.clear());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      caraLock.check();
    }
  });

  caraLock.subscribeToRemoteChanges();
  caraLock.check();
};
